import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const Banking = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bankSettingData, setBankSettingData] = useState([]);

  // Fixed payment methods that cannot be added or removed
  const FIXED_PAYMENT_METHODS = [
    { name: "Bkash", payment_type: "bkash" },
    { name: "Nagad", payment_type: "nagad" },
    { name: "Rocket", payment_type: "rocket" },
    { name: "Upay", payment_type: "upay" }
  ];

  useEffect(() => {
    fetchBankSettingData();
  }, []);

  const fetchBankSettingData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL_API}/admin/getBankDetails`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log(response.data);
      setBankSettingData(response.data);
    } catch (err) {
      console.error("Failed to fetch payment settings:", err);
      setError("Failed to load payment settings.");
      toast.error("Failed to load payment settings.");
    } finally {
      setLoading(false);
    }
  };

  const updateBank = (paymentType) => {
    navigate(`/banking/edit/${paymentType}`);
  };

  const toggleActiveStatus = async (id, currentStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL_API}/admin/updateBankDetails/${id}`,
        { isactive: !currentStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Status updated successfully!");
      fetchBankSettingData(); // Refresh data
    } catch (err) {
      console.error("Failed to update status:", err);
      toast.error("Failed to update status.");
    }
  };


  // Helper function to get method data by payment_type
  const getMethodData = (paymentType) => {
    return bankSettingData?.data?.find(item => item.payment_type === paymentType);
  };

  return (
    <main className="flex ml-0 lg:ml-[230px]  overflow-auto bg-stone-100 pb-8">
      <div className="w-full flex-1 px-5">
        <div className="my-5">
          <div className="flex items-center justify-between">
            <div className="w-auto">
              <h1 className="font mb-3 flex flex-wrap items-center text-lg font-bold text-stone-800 md:text-xl">
                Payment Methods Management
              </h1>
              <p className="text-sm text-gray-600">
                Update details for each payment method (Bkash, Nagad, Rocket, Upay)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a className="font-semibold text-cm-gray" href="/web/banking#">
              Web<span className="ml-2">/</span>
            </a>
            <a className="font-semibold text-cm-gray" href="/web/banking#">
              Payment Methods
            </a>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <p>Loading payment methods...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FIXED_PAYMENT_METHODS.map((method) => {
              const methodData = getMethodData(method.payment_type);
              const hasData = !!methodData;

              return (
                <div key={method.payment_type} className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">{method.name}</h2>
                      <span className={`text-sm px-2 py-1 rounded mt-1 inline-block ${hasData ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {hasData ? 'Configured' : 'Not Configured'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {hasData && (
                        <label
                          htmlFor={`is-active-${method.payment_type}`}
                          className="relative inline-block w-11 h-6 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            id={`is-active-${method.payment_type}`}
                            className="peer sr-only"
                            checked={methodData.isactive}
                            onChange={() => toggleActiveStatus(methodData.id, methodData.isactive)}
                          />
                          <span className="absolute inset-0 bg-gray-200 rounded-full transition-colors duration-200 ease-in-out peer-checked:bg-blue-600 dark:bg-neutral-700 dark:peer-checked:bg-blue-500 peer-disabled:opacity-50 peer-disabled:pointer-events-none" />
                          <span className="absolute top-1/2 start-0.5 -translate-y-1/2 size-5 bg-white rounded-full shadow-xs transition-transform duration-200 ease-in-out peer-checked:translate-x-full dark:bg-neutral-400 dark:peer-checked:bg-white" />
                        </label>
                      )}
                      <button
                        onClick={() => updateBank(method.payment_type)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        {hasData ? 'Update Details' : 'Configure'}
                      </button>
                    </div>
                  </div>

                  {hasData && (
                    <div className="space-y-3 mt-4 border-t pt-4">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500">Account Holder</p>
                          <p className="font-medium text-gray-900">{methodData.account_holder_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Account Number</p>
                          <p className="font-medium text-gray-900">{methodData.account_number || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">UPI ID</p>
                          <p className="font-medium text-gray-900">{methodData.upi_id || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Min/Max Amount</p>
                          <p className="font-medium text-gray-900">
                            {methodData.min_amount || 0} - {methodData.max_amount || 0}
                          </p>
                        </div>
                      </div>

                      {methodData.qr_img && (
                        <div className="mt-4">
                          <p className="text-gray-500 text-sm mb-2">QR Code</p>
                          <img
                            src={
                              methodData?.qr_img?.startsWith("data:image")
                                ? methodData.qr_img
                                : `${process.env.REACT_APP_BASE_URL_API}/uploads/QrCodes/${methodData.qr_img}`
                            }
                            alt={`${method.name} QR Code`}
                            className="w-32 h-32 object-contain border border-gray-300 rounded-lg"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {!hasData && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
                      <p className="text-gray-500 text-sm">
                        No details configured for {method.name} yet. Click "Configure" to add details.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

export default Banking;
